import Color from 'color'
import { ThemeColor } from './theme.types'

export type ThemeColors = { [key in ThemeColor]: string }

export function generateThemeVars(
  colors: ThemeColors,
  mode: 'light' | 'dark' = 'light'
) {
  const adjust = (
    hex: string,
    opts: { lightness?: number; saturation?: number; alpha?: number } = {}
  ) => {
    let col = Color(hex)
    if (opts.saturation !== undefined) {
      // @ts-ignore
      const color = col.hsl().color as [number, number, number]
      let s = color[1] + opts.saturation
      s = Math.max(0, Math.min(100, s))
      col = Color.hsl(color[0], s, color[2])
    }
    if (opts.lightness !== undefined) {
      // @ts-ignore
      const color = col.hsl().color as [number, number, number]
      let l = color[2] + opts.lightness
      l = Math.max(0, Math.min(100, l))
      col = Color.hsl(color[0], color[1], l)
    }
    // Добавление поддержки прозрачности
    if (opts.alpha !== undefined) {
      col = col.alpha(opts.alpha / 100) // Принимаем значение от 0 до 100
    }

    // Возвращаем rgba при наличии альфа-канала
    return col.hex()
  }

  const base = colors[ThemeColor.Base]
  const essential = colors[ThemeColor.Essential]
  const brand = colors[ThemeColor.Brand]
  const system = colors[ThemeColor.System]
  const error = colors[ThemeColor.Error]
  const warning = colors[ThemeColor.Warning]
  const success = colors[ThemeColor.Success]
  const info = colors[ThemeColor.Info]

  if (mode === 'light') {
    return {
      // POPUP
      '--popup-bg': 'rgba(44, 44, 54, 0.6)',
      // GLOBAL
      '--global-base': base,
      '--global-essential': essential,
      '--global-brand': brand,
      '--global-system': system,
      '--global-warning': warning,
      '--global-success': success,
      '--global-error': error,
      '--global-info': info,
      // TYPO
      '--typo-primary': base,
      '--typo-secondary': adjust(system, { lightness: -35 }),
      '--typo-tertiary': adjust(essential, { lightness: -15 }),
      '--typo-contrast': essential,
      '--typo-brand': brand,
      '--typo-brand-light': adjust(brand, { lightness: 52 }),
      '--typo-success': success,
      '--typo-warning': warning,
      '--typo-error': error,
      '--typo-info': info,
      // BG
      '--bg-default': adjust(brand, { lightness: 58 }),
      '--bg-secondary': essential,
      '--bg-tertiary': system,
      '--bg-brand': brand,
      '--bg-brand-light': adjust(brand, { lightness: 52 }),
      '--bg-brand-light-for-slider': adjust(brand, {
        lightness: 22,
      }),
      '--bg-contrast': base,
      '--bg-success': success,
      '--bg-success-light': adjust(success, { lightness: 65 }),
      '--bg-warning': warning,
      '--bg-warning-light': adjust(warning, { lightness: 40 }),
      '--bg-error': error,
      '--bg-error-light': adjust(error, { lightness: 49 }),
      '--bg-info': info,
      '--bg-info-light': adjust(info, { lightness: 52 }),
      '--bg-brand-gradient-light': adjust(brand, { lightness: 17 }),
      '--bg-brand-gradient-dark': adjust(brand, { saturation: -1 }),
      // BORDER
      '--border-default': adjust(essential, { lightness: -10 }),
      '--border-success': success,
      '--border-error': error,
      '--border-warning': warning,
      '--border-info': info,
      '--border-brand': brand,
      '--border-contrast': base,
      '--border-divider': adjust(essential, { lightness: -10 }),
      // CONTROL DEFAULT
      '--control-default-bg': essential,
      '--control-default-bg-hover': system,
      '--control-default-typo': base,
      '--control-default-typo-placeholder': adjust(system, {
        lightness: -35,
      }),
      '--control-default-border': adjust(essential, {
        lightness: -10,
      }),
      '--control-default-border-negative': error,
      '--control-default-typo-negative': error,
      '--control-default-border-active': brand,
      '--control-default-typo-brand': brand,
      // CONTROL PRIMARY
      '--control-primary-bg': brand,
      '--control-primary-typo': essential,
      '--control-primary-bg-hover': adjust(brand, {
        lightness: -5,
      }),
      '--control-primary-bg-negative': error,
      '--control-primary-bg-negative-hover': adjust(error, {
        lightness: -9,
      }),
      '--control-primary-bg-disabled': adjust(brand, { alpha: -0.5 }),
      // CONTROL SECONDARY
      '--control-secondary-bg': essential,
      '--control-secondary-typo': brand,
      '--control-secondary-border': brand,
      '--control-secondary-bg-negative-hover': adjust(error, {
        lightness: 37,
      }),
      '--control-secondary-bg-hover': adjust(brand, {
        lightness: 52,
      }),
      '--control-secondary-border-negative': error,
      '--control-secondary-typo-negative': error,
      '--control-secondary-bg-disabled': adjust(essential, { alpha: -0.5 }),
      '--control-secondary-border-disabled': adjust(brand, { alpha: -0.5 }),
      '--control-secondary-typo-disabled': adjust(brand, { alpha: -0.5 }),
      // CONTROL TERTIARY
      '--control-tertiary-bg': system,
      '--control-tertiary-typo': base,
      '--control-tertiary-bg-hover': adjust(brand, {
        lightness: 52,
      }),
      '--control-tertiary-typo-hover': brand,
      '--control-tertiary-bg-selected': adjust(brand, {
        lightness: -5,
      }),
      '--control-tertiary-typo-selected': essential,
      '--control-tertiary-typo-disabled': adjust(base, { alpha: -0.5 }),
      // CONTROL GHOST
      '--control-ghost-bg': essential,
      '--control-ghost-bg-hover': system,
      '--control-ghost-typo': base,
      '--control-ghost-typo-disabled': adjust(base, { alpha: -0.5 }),
    }
  } else {
    return {
      // POPUP
      '--popup-bg': 'rgba(120, 120, 120, 0.6)',
      // GLOBAL
      '--global-base': base,
      '--global-essential': essential,
      '--global-brand': brand,
      '--global-system': system,
      '--global-warning': warning,
      '--global-success': success,
      '--global-error': error,
      '--global-info': info,
      // TYPO
      '--typo-primary': base,
      '--typo-secondary': adjust(system, {
        saturation: 3,
        lightness: -35,
      }),
      '--typo-tertiary': adjust(base, { lightness: -15 }),
      '--typo-contrast': essential,
      '--typo-brand': brand,
      '--typo-brand-light': adjust(brand, {
        saturation: -32,
        lightness: -40,
      }),
      '--typo-success': success,
      '--typo-warning': warning,
      '--typo-error': error,
      '--typo-info': info,
      // BG
      '--bg-default': adjust(essential, { lightness: -6 }),
      '--bg-secondary': essential,
      '--bg-tertiary': adjust(essential, { lightness: 11 }),
      '--bg-brand': brand,
      '--bg-brand-light': adjust(brand, {
        saturation: -32,
        lightness: -40,
      }),
      '--bg-brand-light-for-slider': adjust(brand, {
        lightness: -22,
      }),
      '--bg-contrast': base,
      '--bg-success': success,
      '--bg-warning': warning,
      '--bg-error': error,
      '--bg-success-light': adjust(success, {
        saturation: -50,
        lightness: -27,
      }),
      '--bg-warning-light': adjust(warning, {
        saturation: -61,
        lightness: -25,
      }),
      '--bg-error-light': adjust(error, {
        saturation: -68,
        lightness: -30,
      }),
      '--bg-info': info,
      '--bg-info-light': adjust(info, {
        saturation: -32,
        lightness: -40,
      }),
      '--bg-brand-gradient-light': adjust(brand, { lightness: 17 }),
      '--bg-brand-gradient-dark': adjust(brand, { saturation: -1 }),
      // BORDER
      '--border-default': adjust(essential, { lightness: 11 }),
      '--border-success': success,
      '--border-error': error,
      '--border-warning': warning,
      '--border-info': info,
      '--border-brand': brand,
      '--border-contrast': base,
      '--border-divider': adjust(essential, { lightness: 11 }),
      // CONTROL DEFAULT
      '--control-default-bg': essential,
      '--control-default-typo': base,
      '--control-default-typo-placeholder': adjust(system, {
        lightness: -35,
      }),
      '--control-default-border': adjust(system, {
        lightness: -35,
      }),
      '--control-default-border-negative': error,
      '--control-default-border-active': brand,
      '--control-default-typo-brand': brand,
      '--control-default-bg-hover': adjust(essential, {
        lightness: 11,
      }),
      '--control-default-typo-negative': error,
      // CONTROL PRIMARY
      '--control-primary-bg': brand,
      '--control-primary-typo': base,
      '--control-primary-bg-hover': adjust(brand, {
        lightness: -5,
      }),
      '--control-primary-bg-negative': error,
      '--control-primary-bg-negative-hover': adjust(error, {
        lightness: -9,
      }),
      '--control-primary-bg-disabled': adjust(brand, { alpha: -0.5 }),
      // CONTROL SECONDARY
      '--control-secondary-bg': essential,
      '--control-secondary-typo': brand,
      '--control-secondary-border': brand,
      '--control-secondary-bg-hover': adjust(essential, {
        lightness: 11,
      }),
      '--control-secondary-typo-negative': error,
      '--control-secondary-border-negative': error,
      '--control-secondary-bg-negative-hover': adjust(error, {
        saturation: -68,
        lightness: -30,
      }),
      '--control-secondary-bg-disabled': adjust(essential, { alpha: -0.5 }),
      '--control-secondary-border-disabled': adjust(brand, { alpha: -0.5 }),
      '--control-secondary-typo-disabled': adjust(brand, { alpha: -0.5 }),
      // CONTROL TERTIARY
      '--control-tertiary-bg': adjust(essential, {
        lightness: 11,
      }),
      '--control-tertiary-typo': base,
      '--control-tertiary-bg-hover': adjust(brand, {
        saturation: -32,
        lightness: -40,
      }),
      '--control-tertiary-typo-hover': brand,
      '--control-tertiary-bg-selected': brand,
      '--control-tertiary-typo-selected': base,
      '--control-tertiary-typo-disabled': adjust(base, { alpha: -0.5 }),
      // CONTROL GHOST
      '--control-ghost-bg': essential,
      '--control-ghost-bg-hover': adjust(essential, {
        lightness: 11,
      }),
      '--control-ghost-typo': base,
      '--control-ghost-typo-disabled': adjust(base, { alpha: -0.5 }),
    }
  }
}
